import { useState } from "react";
import { useLocation as useRouterLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { post } from "../lib/api";
import { leadSourceFields } from "../lib/leadSource";
import { trackEvent } from "../lib/analytics";
import { measureLead } from "../lib/openaiPixel";
import { Eyebrow, MaskLine } from "../components/ui-bits";
import BookMeeting from "../components/BookMeeting";
import { useLang } from "../i18n/LangContext";

const field =
  "w-full rounded-lg border border-sand bg-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-fog/60 focus:border-verde-700";
const label = "mb-1.5 block text-xs font-medium text-ink";

const COPY = {
  en: {
    eyebrow: "Contact", headline: "Talk to Verde Capital.",
    sub: "Have a project, a deal, or a question? We read everything and reply personally.",
    faster: "Faster paths",
    book: "Book a meeting",
    apply: "Get indicative terms →",
    broker: "Submit a broker deal →",
    expert: "Join the Expert Network →",
    done: "Message received.", doneCopy: "Verde Capital will reply shortly.",
    iam: "I am a…",
    audiences: ["Developer", "Broker", "Expert", "Capital Partner", "Other"],
    name: "Name *", email: "Email *", message: "Message *",
    err: "Something went wrong. Please try again.",
    sending: "Sending…", submit: "Send message",
  },
  pt: {
    eyebrow: "Contacto", headline: "Fale com a Verde Capital.",
    sub: "Tem um projeto, um negócio ou uma pergunta? Lemmos tudo e respondemos pessoalmente.",
    faster: "Atalhos mais rápidos",
    book: "Marcar reunião",
    apply: "Obter condições indicativas →",
    broker: "Apresentar negócio de intermediação →",
    expert: "Entrar na Rede de Especialistas →",
    done: "Mensagem recebida.", doneCopy: "A Verde Capital responderá em breve.",
    iam: "Eu sou…",
    audiences: ["Promotor", "Intermediário", "Especialista", "Parceiro de capital", "Outro"],
    name: "Nome *", email: "Email *", message: "Mensagem *",
    err: "Algo falhou. Tente novamente.",
    sending: "A enviar…", submit: "Enviar mensagem",
  },
  nl: {
    eyebrow: "Contact", headline: "Praat met Verde Capital.",
    sub: "Heeft u een project, een deal of een vraag? Wij lezen alles en antwoorden persoonlijk.",
    faster: "Snellere snelkoppelingen",
    book: "Gesprek plannen",
    apply: "Indicatieve voorwaarden aanvragen →",
    broker: "Brokerdeal indienen →",
    expert: "Toetreden tot het Expertennetwerk →",
    done: "Bericht ontvangen.", doneCopy: "Verde Capital reageert zo snel mogelijk.",
    iam: "Ik ben…",
    audiences: ["Ontwikkelaar", "Tussenpersoon", "Expert", "Kapitaalpartner", "Anders"],
    name: "Naam *", email: "E-mail *", message: "Bericht *",
    err: "Er ging iets mis. Probeer het opnieuw.",
    sending: "Versturen…", submit: "Bericht versturen",
  },
};

export default function Contact() {
  const { lang, lp } = useLang();
  const c = COPY[lang];
  const params = new URLSearchParams(useRouterLocation().search);
  const initialAudience = c.audiences.includes(params.get("audience") || "") ? params.get("audience") : "";
  const [form, setForm] = useState({ audience: initialAudience, name: "", email: "", message: "", lang });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.audience && form.name.trim() && /.+@.+\..+/.test(form.email) && form.message.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await post("/contact", { ...form, ...leadSourceFields() });
      trackEvent("contact_form_submit", { audience: form.audience });
      measureLead();
      setDone(true);
    } catch {
      setError(c.err);
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-cream">
      <section className="pt-32 sm:pt-40 pb-10">
        <div className="container-x max-w-3xl">
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h1 className="display mt-6 text-[2.4rem] sm:text-5xl text-charcoal" data-testid="page-headline">
            <MaskLine>{c.headline}</MaskLine>
          </h1>
          <p className="mt-5 text-ink max-w-xl">
            {c.sub}
          </p>
        </div>
      </section>

      <section className="pb-28">
        <div className="container-x max-w-3xl grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-6 text-sm text-ink">
            <div className="border-t border-charcoal/15 pt-6 space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-verde-700">{c.faster}</p>
              <BookMeeting label={c.book} variant="dark" testid="contact-page-book" />
              <Link to={lp("/apply")} className="block font-semibold text-verde-800 hover:underline" data-testid="contact-link-apply">
                {c.apply}
              </Link>
              <Link to={lp("/brokers/submit")} className="block font-semibold text-verde-800 hover:underline" data-testid="contact-link-broker">
                {c.broker}
              </Link>
              <Link to={lp("/expert-network") + "#join"} className="block font-semibold text-verde-800 hover:underline" data-testid="contact-link-expert">
                {c.expert}
              </Link>
            </div>
          </div>

          <div className="lg:col-span-8">
            {done ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-white border border-sand p-10 text-center" data-testid="contact-confirmation">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-verde-800">
                  <Check className="h-5 w-5 text-mint" />
                </span>
                <p className="mt-6 font-display text-2xl font-bold text-charcoal">{c.done}</p>
                <p className="mt-3 text-sm text-ink">{c.doneCopy}</p>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="rounded-2xl bg-white border border-sand p-6 sm:p-10 space-y-6" data-testid="contact-form">
                <div>
                  <label className={label}>{c.iam}</label>
                  <div className="flex flex-wrap gap-2.5">
                    {c.audiences.map((a) => (
                      <button
                        type="button"
                        key={a}
                        onClick={() => setForm((f) => ({ ...f, audience: a }))}
                        data-testid={`contact-audience-${a.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`rounded-full px-5 py-2.5 text-sm border transition-colors duration-200 ${
                          form.audience === a
                            ? "border-verde-800 bg-verde-800 text-white"
                            : "border-sand text-ink hover:border-verde-700"
                        }`}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className={label}>{c.name}</label>
                    <input className={field} value={form.name} onChange={set("name")} data-testid="contact-name" />
                  </div>
                  <div>
                    <label className={label}>{c.email}</label>
                    <input type="email" className={field} value={form.email} onChange={set("email")} data-testid="contact-email" />
                  </div>
                </div>
                <div>
                  <label className={label}>{c.message}</label>
                  <textarea rows={5} className={field} value={form.message} onChange={set("message")} data-testid="contact-message" />
                </div>
                {error && <p className="text-sm text-red-700" data-testid="contact-error">{error}</p>}
                <button
                  type="submit"
                  disabled={!valid || submitting}
                  className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-verde-800 px-8 py-4 text-sm font-semibold text-white transition-all hover:bg-verde-700 disabled:cursor-not-allowed disabled:opacity-40"
                  data-testid="contact-submit"
                >
                  {submitting ? c.sending : c.submit}
                  {!submitting && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
